import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";
import MoveToFolderDialog from "@/components/MoveToFolderDialog";
import {
  Building2, Plus, ChevronRight, ArrowLeft, Trash2, FileText, Calendar, X,
  CheckCircle2, AlertTriangle, DoorOpen,
} from "lucide-react";

/**
 * Properties (Kohteet) page.
 * Routes:
 *  - /app/kohteet                         → list groups
 *  - /app/kohteet/:groupId                → list properties in group
 *  - /app/kohteet/:groupId/:propertyId   → list reports under property
 */
export default function Properties() {
  const { groupId, propertyId } = useParams();

  if (propertyId) return <PropertyReports groupId={groupId} propertyId={propertyId} />;
  if (groupId) return <GroupProperties groupId={groupId} />;
  return <GroupsList />;
}

// --------------- Groups list ---------------
function GroupsList() {
  const navigate = useNavigate();
  const { t } = useT();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addingGroup, setAddingGroup] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/property-groups");
      setGroups(data.groups || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createGroup = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/property-groups", { name: newName.trim() });
      setNewName("");
      setAddingGroup(false);
      toast.success(t("props.groupCreated"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("props.createFailed"));
    }
  };

  const removeGroup = async (g) => {
    if (!confirm(`${t("props.deleteGroupConfirm")} "${g.name}"?`)) return;
    try {
      await api.delete(`/property-groups/${g.group_id}`);
      toast.success(t("props.deleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("props.deleteFailed"));
    }
  };

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto">
      <button
        onClick={() => navigate("/app")}
        className="inline-flex items-center gap-1.5 px-3 py-2 -ml-3 rounded-lg hover:bg-slate-100 text-sm font-semibold text-slate-700 mb-4"
        data-testid="groups-back-to-dashboard"
      >
        <ArrowLeft className="h-4 w-4" /> Takaisin etusivulle
      </button>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">{t("props.eyebrow")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-1">{t("props.groupsTitle")}</h1>
          <p className="mt-2 text-slate-600">{t("props.groupsSub")}</p>
        </div>
        {!addingGroup && (
          <button onClick={() => setAddingGroup(true)} className="btn-primary" data-testid="groups-add">
            <Plus className="h-4 w-4" /> {t("props.newGroup")}
          </button>
        )}
      </div>

      {addingGroup && (
        <div className="card-soft p-4 mb-4 flex items-center gap-2" data-testid="groups-add-form">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createGroup(); if (e.key === "Escape") { setAddingGroup(false); setNewName(""); } }}
            placeholder={t("props.newGroupPh")}
            className="input-base flex-1"
            data-testid="groups-add-input"
          />
          <button onClick={createGroup} className="btn-primary text-sm" data-testid="groups-add-save">{t("props.create")}</button>
          <button onClick={() => { setAddingGroup(false); setNewName(""); }} className="btn-ghost text-sm">{t("props.cancel")}</button>
        </div>
      )}

      {loading && <div className="text-slate-400">{t("props.loading")}</div>}
      {!loading && groups.length === 0 && !addingGroup && (
        <div className="card-soft p-8 text-center">
          <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display font-semibold text-slate-900 mb-1">{t("props.emptyGroups")}</div>
          <div className="text-sm text-slate-500 mb-4">{t("props.emptyGroupsSub")}</div>
          <button onClick={() => setAddingGroup(true)} className="btn-primary">
            <Plus className="h-4 w-4" /> {t("props.newGroup")}
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {groups.map((g) => (
          <div
            key={g.group_id}
            className="card-soft p-5 group hover:border-emerald-300 transition-colors cursor-pointer relative"
            onClick={() => navigate(`/app/kohteet/${g.group_id}`)}
            data-testid={`group-card-${g.group_id}`}
          >
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-slate-900 truncate">{g.name}</div>
                <div className="text-xs text-slate-500">{g.property_count} {t("props.propertiesCount")}</div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500" />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); removeGroup(g); }}
              className="absolute top-2 right-2 p-1.5 rounded-lg text-slate-300 opacity-0 group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-500 transition-all"
              data-testid={`group-delete-${g.group_id}`}
              title={t("props.deleteGroupTitle")}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// --------------- Properties in group ---------------
function GroupProperties({ groupId }) {
  const navigate = useNavigate();
  const { t } = useT();
  const [group, setGroup] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: g }, { data: p }] = await Promise.all([
        api.get("/property-groups"),
        api.get(`/properties?group_id=${groupId}`),
      ]);
      setGroup((g.groups || []).find((x) => x.group_id === groupId));
      setProperties(p.properties || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [groupId]);

  const createProperty = async () => {
    if (!newName.trim()) return;
    try {
      await api.post("/properties", { name: newName.trim(), group_id: groupId });
      setNewName("");
      setAdding(false);
      toast.success(t("props.propertyCreated"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("props.createFailed"));
    }
  };

  const removeProperty = async (p) => {
    if (!confirm(`${t("props.deletePropertyConfirm")} "${p.name}"? ${t("props.deletePropertyTail")}`)) return;
    try {
      await api.delete(`/properties/${p.property_id}`);
      toast.success(t("props.deleted"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("props.deleteFailed"));
    }
  };

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto">
      <button
        onClick={() => navigate("/app/kohteet")}
        className="inline-flex items-center gap-1.5 px-3 py-2 -ml-3 rounded-lg hover:bg-slate-100 text-sm font-semibold text-slate-700 mb-4"
        data-testid="back-to-groups"
      >
        <ArrowLeft className="h-4 w-4" /> {t("props.backToGroups")}
      </button>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">{t("props.groupEyebrow")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-1">{group?.name || "…"}</h1>
          <p className="mt-2 text-slate-600">{properties.length} {t("props.propertiesCount")}</p>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} className="btn-primary" data-testid="property-add">
            <Plus className="h-4 w-4" /> {t("props.newProperty")}
          </button>
        )}
      </div>

      {adding && (
        <div className="card-soft p-4 mb-4 flex items-center gap-2">
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") createProperty(); if (e.key === "Escape") { setAdding(false); setNewName(""); } }}
            placeholder={t("props.newPropertyPh")}
            className="input-base flex-1"
            data-testid="property-add-input"
          />
          <button onClick={createProperty} className="btn-primary text-sm">{t("props.create")}</button>
          <button onClick={() => { setAdding(false); setNewName(""); }} className="btn-ghost text-sm">{t("props.cancel")}</button>
        </div>
      )}

      {loading && <div className="text-slate-400">{t("props.loading")}</div>}
      {!loading && properties.length === 0 && !adding && (
        <div className="card-soft p-8 text-center">
          <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display font-semibold text-slate-900 mb-1">{t("props.emptyProperties")}</div>
          <div className="text-sm text-slate-500 mb-4">{t("props.emptyPropertiesSub")}</div>
          <button onClick={() => setAdding(true)} className="btn-primary"><Plus className="h-4 w-4" /> {t("props.newProperty")}</button>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        {properties.map((p) => (
          <div
            key={p.property_id}
            className="card-soft p-4 group hover:border-emerald-300 transition-colors cursor-pointer relative"
            onClick={() => navigate(`/app/kohteet/${groupId}/${p.property_id}`)}
            data-testid={`property-card-${p.property_id}`}
          >
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Building2 className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-500">
                  {p.report_count} {t("props.reportsCount")}
                  {p.address && <span> · {p.address}</span>}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500" />
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); removeProperty(p); }}
              className="absolute top-2 right-2 p-1.5 rounded-lg text-slate-300 opacity-0 group-hover:opacity-100 hover:bg-rose-50 hover:text-rose-500 transition-all"
              data-testid={`property-delete-${p.property_id}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// --------------- Reports under property ---------------
function PropertyReports({ groupId, propertyId }) {
  const navigate = useNavigate();
  const { t, isEn } = useT();
  const [property, setProperty] = useState(null);
  const [group, setGroup] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [moveOpen, setMoveOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [{ data: g }, { data: p }, { data: rs }] = await Promise.all([
          api.get("/property-groups"),
          api.get("/properties"),
          api.get("/reports"),
        ]);
        setGroup((g.groups || []).find((x) => x.group_id === groupId));
        setProperty((p.properties || []).find((x) => x.property_id === propertyId));
        setReports((rs || []).filter((r) => r.property_id === propertyId));
      } finally {
        setLoading(false);
      }
    })();
  }, [groupId, propertyId]);

  const toggle = (rid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid);
      else next.add(rid);
      return next;
    });
  };
  const allSelected = reports.length > 0 && selectedIds.size === reports.length;
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(reports.map((r) => r.report_id)));
  };

  return (
    <div className="p-5 md:p-8 max-w-5xl mx-auto">
      {/* Selkeä paluunappi vasempaan yläkulmaan */}
      <button
        onClick={() => navigate(`/app/kohteet/${groupId}`)}
        className="inline-flex items-center gap-1.5 px-3 py-2 -ml-3 rounded-lg hover:bg-slate-100 text-sm font-semibold text-slate-700 mb-4"
        data-testid="property-back-button"
      >
        <ArrowLeft className="h-4 w-4" /> Takaisin kohteisiin
      </button>

      <div className="text-xs text-slate-400 flex items-center gap-1.5 mb-4">
        <Link to="/app/kohteet" className="hover:text-slate-700">{t("props.backToGroups")}</Link>
        <ChevronRight className="h-3 w-3" />
        <Link to={`/app/kohteet/${groupId}`} className="hover:text-slate-700">{group?.name || "…"}</Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-slate-700 font-semibold truncate">{property?.name || "…"}</span>
      </div>

      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold">{t("props.propertyEyebrow")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-slate-900 mt-1">{property?.name || "…"}</h1>
          <p className="mt-2 text-slate-600">{reports.length} {t("props.reportsCount")}</p>
        </div>
        <button
          onClick={() => navigate("/app/uusi", { state: { property_id: propertyId } })}
          className="btn-primary"
          data-testid="property-new-report"
        >
          <Plus className="h-4 w-4" /> {t("props.createReportHere")}
        </button>
      </div>

      {loading && <div className="text-slate-400">{t("props.loading")}</div>}
      {!loading && reports.length === 0 && (
        <div className="card-soft p-8 text-center">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display font-semibold text-slate-900 mb-1">{t("props.emptyReports")}</div>
          <div className="text-sm text-slate-500 mb-4">{t("props.emptyReportsSub")}</div>
          <button
            onClick={() => navigate("/app/uusi", { state: { property_id: propertyId } })}
            className="btn-primary"
            data-testid="property-new-report-empty"
          >
            <Plus className="h-4 w-4" /> {t("props.createReport")}
          </button>
        </div>
      )}

      {reports.length > 0 && (
        <div className="flex items-center gap-2 mb-2 text-xs text-slate-500">
          <label className="inline-flex items-center gap-2 cursor-pointer px-2 py-1 rounded-lg hover:bg-slate-100" data-testid="property-select-all">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500"
            />
            <span>{allSelected ? "Poista kaikki valinnat" : "Valitse kaikki"}</span>
          </label>
        </div>
      )}

      <div className="space-y-2">
        {reports.map((r) => {
          const checked = selectedIds.has(r.report_id);
          return (
            <div
              key={r.report_id}
              className={`card-soft p-3 flex items-center gap-3 transition-colors ${checked ? "border-emerald-300 bg-emerald-50/60" : "hover:border-emerald-300"}`}
              data-testid={`property-report-${r.report_id}`}
            >
              <label className="flex items-center flex-shrink-0 cursor-pointer p-1 -m-1" data-testid={`property-report-select-${r.report_id}`}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(r.report_id)}
                  className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500"
                />
              </label>
              <Link
                to={`/app/raportti/${r.report_id}`}
                className="flex items-center gap-3 flex-1 min-w-0"
              >
                <div className="h-9 w-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    <Calendar className="h-3 w-3" />
                    {r.created_at ? new Date(r.created_at).toLocaleDateString(isEn ? "en-GB" : "fi-FI") : ""}
                    <span className="text-slate-300">·</span>
                    <span>{(r.units || []).length} {t("props.unitsCount")}</span>
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-slate-300" />
              </Link>
            </div>
          );
        })}
      </div>

      {selectedIds.size > 0 && (
        <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center pointer-events-none" data-testid="bulk-action-bar">
          <div className="pointer-events-auto max-w-lg w-[calc(100%-2rem)] mx-4 rounded-2xl bg-slate-900 text-white shadow-2xl ring-1 ring-black/10 px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold tabular-nums">{selectedIds.size}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold leading-tight">{selectedIds.size === 1 ? "1 työ valittu" : `${selectedIds.size} työtä valittu`}</div>
              <div className="text-[11px] text-white/60 mt-0.5">Siirrä toiseen kohteeseen yhdellä toiminnolla.</div>
            </div>
            <button
              onClick={() => setSelectedIds(new Set())}
              className="p-2 rounded-lg hover:bg-white/10 text-white/80"
              title="Tyhjennä valinta"
              data-testid="bulk-clear"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              onClick={() => setMoveOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white text-sm font-semibold whitespace-nowrap"
              data-testid="bulk-move"
            >
              <Building2 className="h-4 w-4" /> Siirrä
            </button>
          </div>
        </div>
      )}

      {moveOpen && (
        <MoveToFolderDialog
          kind="bulk-reports"
          itemIds={Array.from(selectedIds)}
          currentPropertyId={propertyId}
          onClose={() => setMoveOpen(false)}
          onMoved={(newPid) => {
            // Poista siirretyt raportit tästä kohteesta niin että ne katoavat
            // näkymästä välittömästi. Jos käyttäjä oli valinnut kohteen joka
            // on sama kuin nykyinen, ei kadota mitään.
            if (newPid !== propertyId) {
              const moved = new Set(selectedIds);
              setReports((arr) => arr.filter((r) => !moved.has(r.report_id)));
            }
            setSelectedIds(new Set());
          }}
        />
      )}
    </div>
  );
}
